/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */
import { Box, Button, Checkbox, Grid, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { ThumbDown, ThumbUp } from '@material-ui/icons';
import React from 'react';
import { observer } from 'mobx-react';
import { EntryContentViewModel } from '../EntryContentViewModel';
import { ENTRY_REVIEW_STATE } from '../../../../../../../types/course/Entry';
import { useViewModel } from '../../../../../../../hooks/useViewModel';
import EntryContentViewStyles from '../EntryContentViewStyles';

interface Props {
	entryState: ENTRY_REVIEW_STATE;
	entryId: string;
}

export const EntryContentButtonBoxView = observer((props: Props) => {
	const classes = EntryContentViewStyles();
	const viewModel: EntryContentViewModel = useViewModel(EntryContentViewModel);
	const { entryId, entryState } = props;

	return (
		<>
			{viewModel.isPublishingView() ? (
				<form
					onSubmit={(e) => {
						e.preventDefault();
						viewModel.requestPublication(entryId);
					}}
					className={classes.requestContainer}>
					<Box className={classes.checkboxContainer}>
						<Checkbox
							className={classes.checkbox}
							onChange={() => viewModel.toggleIsValidData()}
							value={viewModel.isValidData()}
						/>
						<label className={classes.checkboxLabel}>
							<FormattedMessage id={'entries.valid_data'} />
						</label>
					</Box>
					<Button
						className={classes.requestPublicationButton}
						variant={'contained'}
						color={'secondary'}
						disabled={!viewModel.isValidData()}
						type={'submit'}>
						<Typography>
							<FormattedMessage id={'entries.request_publication'} />
						</Typography>
					</Button>
				</form>
			) : (
				<Box>
					{entryState !== ENTRY_REVIEW_STATE.APPROVED && (
						<Grid container className={classes.thumbContainer}>
							{entryState === ENTRY_REVIEW_STATE.PENDING && (
								<Grid item xs={6} className={classes.thumbBox}>
									<Button
										fullWidth={true}
										className={classes.rejectButton}
										onClick={() => {
											viewModel.updateReviewState(entryId, ENTRY_REVIEW_STATE.REJECTED, entryState);
										}}>
										<ThumbDown />
										<Typography>
											<FormattedMessage id={'entries.reject'} />
										</Typography>
									</Button>
								</Grid>
							)}
							<Grid item xs={6} className={classes.thumbBox}>
								<Button
									fullWidth={true}
									className={classes.approveButton}
									onClick={() => {
										viewModel.updateReviewState(entryId, ENTRY_REVIEW_STATE.APPROVED, entryState);
									}}>
									<ThumbUp />
									<Typography>
										<FormattedMessage id={'entries.approve'} />
									</Typography>
								</Button>
							</Grid>
						</Grid>
					)}
				</Box>
			)}
		</>
	);
});
